import { useEffect, useState } from "react";
import {
  Paper,
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Chip,
  Snackbar,
  Alert,
  Link,
} from "@mui/material";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import {
  listPrinters,
  printZpl,
  getPrinterConfig,
  savePrinterConfig,
  isQzConnected,
} from "../../services/qzPrint";
import { LABEL_SIZES, DPI_OPTIONS, PRINT_LANGUAGES, buildTestLabel } from "../../utils/buildBultoZpl";

// Configuración de la impresora térmica de ESTA PC (vía QZ Tray). Se guarda en
// localStorage porque cada estación de packing tiene su propia impresora.
const PrinterConfigCard = () => {
  const initial = getPrinterConfig();
  const [printers, setPrinters] = useState([]);
  const [printerName, setPrinterName] = useState(initial.printerName || "");
  const [dpi, setDpi] = useState(initial.dpi || 203);
  const [labelSize, setLabelSize] = useState(initial.labelSize || "10x15");
  const [language, setLanguage] = useState(initial.language || "tspl");
  const [labelFormat, setLabelFormat] = useState(initial.labelFormat || "pdf");
  const [detecting, setDetecting] = useState(false);
  const [qzOk, setQzOk] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: "success", message: "" });

  const notify = (severity, message) => setSnack({ open: true, severity, message });

  useEffect(() => {
    setQzOk(isQzConnected());
  }, []);

  const handleDetect = async () => {
    setDetecting(true);
    try {
      const found = await listPrinters();
      setPrinters(found);
      setQzOk(true);
      if (!found.length) {
        notify("warning", "QZ Tray está abierto pero no detectó impresoras instaladas.");
      } else {
        notify("success", `Se detectaron ${found.length} impresora(s).`);
      }
    } catch (err) {
      setQzOk(false);
      notify(
        "error",
        "No se pudo conectar con QZ Tray. Verificá que esté instalado y abierto en esta PC."
      );
    } finally {
      setDetecting(false);
    }
  };

  const handleSave = () => {
    savePrinterConfig({ printerName, dpi: Number(dpi), labelSize, language, labelFormat });
    notify("success", "Configuración de impresora guardada en esta PC.");
  };

  const handleTest = async () => {
    if (!printerName) {
      notify("warning", "Elegí una impresora primero (Detectar impresoras).");
      return;
    }
    try {
      savePrinterConfig({ printerName, dpi: Number(dpi), labelSize, language, labelFormat });
      await printZpl(printerName, buildTestLabel({ language, dpi: Number(dpi), labelSize }));
      notify("success", `Etiqueta de prueba (${language.toUpperCase()}) enviada a la impresora.`);
    } catch (err) {
      notify("error", "No se pudo imprimir. ¿QZ Tray abierto y la impresora encendida?");
    }
  };

  return (
    <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #e0e0e0", overflow: "hidden" }}>
      <Box
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          bgcolor: "grey.50",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PrintOutlinedIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1" fontWeight={600}>
            Impresora de etiquetas (esta PC)
          </Typography>
        </Box>
        {labelFormat === "pdf" ? (
          <Chip size="small" label="PDF (descarga)" color="info" variant="filled" />
        ) : (
          <Chip
            size="small"
            label={qzOk ? "QZ Tray conectado" : "QZ Tray sin detectar"}
            color={qzOk ? "success" : "default"}
            variant={qzOk ? "filled" : "outlined"}
          />
        )}
      </Box>

      <Box sx={{ px: 3, py: 2.5 }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="format-label">Formato de etiqueta</InputLabel>
              <Select
                labelId="format-label"
                label="Formato de etiqueta"
                value={labelFormat}
                onChange={(e) => setLabelFormat(e.target.value)}
              >
                <MenuItem value="direct">Impresión directa (térmica — ZPL/TSPL)</MenuItem>
                <MenuItem value="pdf">PDF (descarga — impresora normal)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              {labelFormat === "pdf"
                ? "Las etiquetas de bulto se descargan como PDF al confirmar el pedido. No requiere QZ Tray ni impresora configurada."
                : "La etiqueta sale directo en la impresora térmica de esta PC vía QZ Tray. Si falla, se descarga en PDF como respaldo."}
            </Typography>
          </Grid>
        </Grid>

        {labelFormat === "direct" && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
            La impresión usa <strong>QZ Tray</strong>, una app gratis que se instala una vez en
            esta PC.{" "}
            <Link href="https://qz.io/download/" target="_blank" rel="noopener">
              Descargar QZ Tray
            </Link>
            . Abrila, tocá "Detectar impresoras" y elegí la tuya.
          </Typography>
        )}

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="printer-label">Impresora</InputLabel>
              <Select
                labelId="printer-label"
                label="Impresora"
                value={printerName}
                disabled={labelFormat === "pdf"}
                onChange={(e) => setPrinterName(e.target.value)}
              >
                {printerName && !printers.includes(printerName) && (
                  <MenuItem value={printerName}>{printerName}</MenuItem>
                )}
                {printers.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
                {!printers.length && !printerName && (
                  <MenuItem value="" disabled>
                    (Tocá "Detectar impresoras")
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="size-label">Tamaño</InputLabel>
              <Select
                labelId="size-label"
                label="Tamaño"
                value={labelSize}
                disabled={labelFormat === "pdf"}
                onChange={(e) => setLabelSize(e.target.value)}
              >
                {Object.entries(LABEL_SIZES).map(([key, s]) => (
                  <MenuItem key={key} value={key}>
                    {s.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="dpi-label">DPI</InputLabel>
              <Select
                labelId="dpi-label"
                label="DPI"
                value={dpi}
                disabled={labelFormat === "pdf"}
                onChange={(e) => setDpi(e.target.value)}
              >
                {DPI_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="lang-label">Lenguaje de impresora</InputLabel>
              <Select
                labelId="lang-label"
                label="Lenguaje de impresora"
                value={language}
                disabled={labelFormat === "pdf"}
                onChange={(e) => setLanguage(e.target.value)}
              >
                {PRINT_LANGUAGES.map((l) => (
                  <MenuItem key={l.value} value={l.value}>
                    {l.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Si no sabés cuál, probá ambos con "Probar impresión" y quedate con el que imprima la
              etiqueta legible (las Xprinter suelen ser <strong>TSPL</strong>; Zebra es ZPL).
            </Typography>
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 2.5 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleDetect}
            disabled={detecting || labelFormat === "pdf"}
          >
            {detecting ? "Detectando..." : "Detectar impresoras"}
          </Button>
          <Button variant="contained" size="small" onClick={handleSave}>
            Guardar
          </Button>
          <Button
            variant="text"
            size="small"
            onClick={handleTest}
            disabled={!printerName || labelFormat === "pdf"}
          >
            Probar impresión
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default PrinterConfigCard;
